import { useCallback, useMemo, useState } from 'react';

import { useIntl } from '@futrli/localisation';

import {
  SortConfig,
  buildSortOptions,
  parseSortOptionValue,
  toSortOptionValue,
  useFilteredData,
  FilterStep,
  useListFilterPersistence,
  readPersistedFilters
} from '../../../../../utils/list-filters';
import {
  REPORTS_SORT_COLUMNS,
  REPORTS_COMPARATORS,
  DEFAULT_REPORTS_SORT,
  HasName,
  HasCreated
} from '../../../../../reports/components/tabs/shared/sort-config';

export type StatusFilterValue = 'all' | 'published' | 'draft';

export const DASHBOARD_TEMPLATES_STORAGE_KEY = 'dashboard-templates-filter-v1';

interface StatusFilterConfig<T extends HasName & HasCreated> {
  data: T[];
  statusAccessor: (item: T) => string;
  publishedValue: string;
  nameAccessor: (item: T) => string;
  storageKey: string;
  organisationId: string;
}

export const useStatusFilter = <T extends HasName & HasCreated>({
  data,
  statusAccessor,
  publishedValue,
  nameAccessor,
  storageKey,
  organisationId
}: StatusFilterConfig<T>) => {
  const intl = useIntl();

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilterValue>(() => {
    const persisted = readPersistedFilters(storageKey, organisationId);
    return (persisted?.filters?.statusFilter as StatusFilterValue) ?? 'all';
  });
  const [sortConfig, setSortConfig] = useState<SortConfig>(() => {
    const persisted = readPersistedFilters(storageKey, organisationId);
    return persisted?.sort ?? DEFAULT_REPORTS_SORT;
  });

  const filters = useMemo(() => ({ statusFilter }), [statusFilter]);

  const handleRestore = useCallback(
    (restoredFilters: { statusFilter: StatusFilterValue }, restoredSort: SortConfig | undefined) => {
      setStatusFilter(restoredFilters.statusFilter);
      if (restoredSort) {
        setSortConfig(restoredSort);
      }
    },
    []
  );

  useListFilterPersistence({
    storageKey,
    organisationId,
    filters,
    sort: sortConfig,
    onRestore: handleRestore
  });

  const sortOptions = useMemo(() => buildSortOptions(REPORTS_SORT_COLUMNS, intl), [intl]);
  const sortValue = useMemo(() => toSortOptionValue(sortConfig), [sortConfig]);

  const handleSortChange = useCallback((value: string) => {
    setSortConfig(parseSortOptionValue(value));
  }, []);

  const searchAccessors = useMemo(() => [nameAccessor], [nameAccessor]);

  const filterSteps: FilterStep<T>[] = useMemo(
    () => [
      {
        type: 'custom' as const,
        apply: (items: T[]) => {
          if (statusFilter === 'all') {
            return items;
          }
          if (statusFilter === 'published') {
            return items.filter(item => statusAccessor(item) === publishedValue);
          }
          return items.filter(item => statusAccessor(item) === 'draft');
        }
      }
    ],
    [statusFilter, statusAccessor, publishedValue]
  );

  const filteredData = useFilteredData<T>({
    allData: data,
    searchTerm,
    searchAccessors,
    filterSteps,
    sortConfig,
    comparators: REPORTS_COMPARATORS
  });

  return {
    filteredData,
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    sortValue,
    sortOptions,
    handleSortChange
  };
};
